import { Card, Icon, Intent, Spinner } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import {
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
  UNKNOWN_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { getDataSourceTypeCardTestId } from 'src/constants/testIds';
import { connectionSelector } from 'src/selectors/connection';
import { currentStardogVersionSelector } from 'src/selectors/currentStardogVersion';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import * as copy from 'src/templates/copy';
import { getDataSourceIcon } from 'src/templates/icons';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceTypeSelectionPanelIconProps = {
  dataSourceType: string;
  isSampleLoading: boolean;
  onClick: () => void;
};

export const DataSourceTypeSelectionPanelIcon: React.VFC<
  DataSourceTypeSelectionPanelIconProps
> = ({ dataSourceType, isSampleLoading, onClick }) => {
  const connection = useReduxSelector(connectionSelector);
  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );
  const stardogVersion = useReduxSelector(currentStardogVersionSelector);

  const isSample = dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE;
  const isLoading = isSample && isSampleLoading;
  const { isBlueprintIcon, icon: iconNameOrData } = getDataSourceIcon({
    dataSourceType,
    stardogVersion,
  });
  const label =
    dataSourceLabelsByType[dataSourceType] ||
    dataSourceLabelsByType[UNKNOWN_DATA_SOURCE_TYPE];
  let icon: React.ReactElement;
  if (isLoading) {
    icon = <Spinner />;
  } else if (isBlueprintIcon) {
    icon = (
      <Icon
        icon={iconNameOrData}
        iconSize={50}
        intent={isSample ? Intent.PRIMARY : undefined}
      />
    );
  } else {
    icon = <img alt={`${label} icon`} height="50px" src={iconNameOrData} />;
  }
  return (
    <Card
      key={dataSourceType}
      className={classNames(
        'sd-data-source-panel-cards__card',
        'sd-data-source-panel-cards__card--type'
      )}
      data-testid={getDataSourceTypeCardTestId(dataSourceType)}
      interactive
      onClick={onClick}
    >
      {icon}
      <div>{label}</div>
      {isSample ? (
        <div className="sd-data-source-panel-cards__card--type__sample-banner">
          {
            copy.components.projectResourceDialog.dataSourceTypeSelectionPanel
              .sampleDataBanner
          }
        </div>
      ) : null}
    </Card>
  );
};
